import { FormGroup } from '@angular/forms';

export interface ClaimDetailItem {
  selected?: boolean;
  selectedKey?: string;
  materialNumber: string;
  unitPrice: string;
  invoiceAmount: string;
  orderLineQty: number;
  deliveryNumber: string;
  dnItemNo: string;
  deliveryQty: number;
  submittableQty: number;
  claimQty: number;
  claimAmount: string;
  receiverNumber: string;
  returnOrderNumber: string;
  orderNo?: string;
  orderItemNo?: string;
  orderTotalAmount?: string;
  orderQuantity?: number;
  invoiceNo?: string;
  invoiceItemNo?: string;
  dnTotalValueWithTax?: string;
  dnQuantity?: number;
  priceDifference?: string;
}

export interface ClaimDetailViewState {
  claimNumber: string;
  claimStatus: string;
  claimStatusName: string;
  claimReferences: any[];
  claimTypeName: string;
  claimCategoryName: string;
  ruleLevel2Name: string;
  contactAddress: string;
  contactName: string;
  contactPhone: string;
  returnSo: string;
  returnDn: string;
  trackingNum: string;
  detailItems: ClaimDetailItem[];
  attachments: { attachmentId: string; name: string }[];
  basicForm: FormGroup;
  otherForm: FormGroup;
  schemeOptions: { label: string; value: string }[];
  reasonOptions?: { label: string; value: string }[];
  categoryOptions?: { label: string; value: string }[];
  indexOptions?: { label: string; value: string }[];
}

export function getClaimStatusLabel(status: string): string {
  switch (status) {
    case 'DRAFT':
      return 'CLAIM_STATUS_DRAFT';
    case 'IN_PROGRESS':
      return 'CLAIM_STATUS_IN_PROGRESS';
    case 'APPROVED':
      return 'CLAIM_STATUS_RECEIVED';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'REJECTED':
      return 'CLAIM_STATUS_REJECTED';
    case 'CANCEL':
      return 'CANCELLED';
    default:
      return status;
  }
}

export function getClaimStatusColor(status: string): { color: string; bg: string } {
  switch (status) {
    case 'DRAFT':
      return { color: '#71767C', bg: '#F2F3F4' };
    case 'IN_PROGRESS':
      return { color: '#FF8726', bg: '#FFF6EE' };
    case 'APPROVED':
      return { color: '#009C5F', bg: '#E6F6EF' };
    case 'COMPLETED':
      return { color: '#18837E', bg: '#F1F8F7' };
    case 'REJECTED':
      return { color: '#ED0007', bg: '#FEEDED' };
    case 'CANCEL':
      return { color: '#007BC0', bg: '#E6F2F9' };
    default:
      return { color: '#A0A0A0', bg: '#F2F3F4' };
  }
}

export function applyClaimDetailVo(state: ClaimDetailViewState, vo: any): void {
  const ruleLevel1 = (vo.ruleLevel1 ?? '').toString().toLowerCase();
  const referenceType = ruleLevel1 === 'consignment' ? 'consignment' : 'normal';

  state.claimNumber = vo.claimNumber ?? state.claimNumber;
  state.claimStatus = vo.claimStatus ?? '';
  state.claimStatusName = vo.claimStatusName ?? '';
  state.claimReferences = Array.isArray(vo.references) ? vo.references : [];

  const reason = vo.claimType ?? null;
  const category = vo.claimCategory ?? null;
  const scheme = vo.ruleLevel2 ?? null;
  const indexNumber = vo.customerReferenceId ?? null;

  state.claimTypeName = vo.claimTypeName ?? '';
  state.claimCategoryName = vo.claimCategoryName ?? '';
  state.ruleLevel2Name = vo.ruleLevel2Name ?? '';
  state.contactAddress = pickFirstString(vo, [
    'contactAddress',
    'customerAddress',
    'customerPickUpAddress',
  ]);
  state.contactName = pickFirstString(vo, [
    'contactName',
    'customerContact',
    'customerPickupContact',
  ]);
  state.contactPhone = pickFirstString(vo, [
    'contactPhone',
    'customerPhone',
    'customerPickupPhone',
  ]);
  state.returnSo = vo.returnSo ?? '';
  state.returnDn = vo.returnDn ?? '';
  state.trackingNum = vo.trackingNum ?? '';

  state.basicForm.patchValue({
    referenceType,
    reason,
    category,
    indexNumber,
    scheme,
    selfReturn: vo.selfReturn === 1 ? 'yes' : 'no',
    customerAddress: pickFirstString(vo, ['customerAddress', 'customerPickUpAddress']),
    customerContact: pickFirstString(vo, ['customerContact', 'customerPickupContact']),
    customerPhone: pickFirstString(vo, ['customerPhone', 'customerPickupPhone']),
  });
  state.otherForm.patchValue({
    remark: vo.comments ?? '',
  });

  ensureOption(state.schemeOptions, scheme);
  if (state.reasonOptions) {
    ensureOption(state.reasonOptions, reason);
  }
  if (state.categoryOptions) {
    ensureOption(state.categoryOptions, category);
  }
  if (state.indexOptions) {
    ensureOption(state.indexOptions, indexNumber);
  }

  state.attachments = Array.isArray(vo.attachments)
    ? vo.attachments
        .filter((att: any) => !!att?.attachmentId)
        .map((att: any) => ({
          attachmentId: `${att.attachmentId}`,
          name: att.fileName ?? att.name ?? `${att.attachmentId}`,
        }))
    : [];

  state.detailItems = mapReferencesToDetailItems(state.claimReferences).filter(
    item => (item.claimQty ?? 0) > 0,
  );
}

function mapReferencesToDetailItems(references: any[]): ClaimDetailItem[] {
  const rows: ClaimDetailItem[] = [];
  references.forEach(ref => {
    const items = Array.isArray(ref?.items) ? ref.items : [];
    const refType = (ref?.referenceType ?? '').toString().toUpperCase();
    const isOrderMode = refType === 'ORDER' || refType === 'CONSIGNMENT';
    items.forEach((item: any) => {
      const qty = Number(item?.claimQuantity ?? 0);
      const unitPrice = Number(item?.unitPriceWithTax ?? item?.unitPrice ?? 0);
      rows.push({
        selected: qty > 0,
        selectedKey: buildSelectedKey(item, refType, ref?.referenceNo),
        materialNumber: item?.materialCode ?? item?.materialNumber ?? '',
        unitPrice: formatMoney(unitPrice, item?.currency),
        invoiceAmount: formatMoney(
          Number(item?.dnTotalValueWithTax ?? item?.unitPriceWithTax ?? 0),
          item?.currency,
        ),
        orderLineQty: Number(item?.dnQuantity ?? 0),
        deliveryNumber: item?.dnNo ?? (isOrderMode ? '' : (item?.referenceNo ?? ref?.referenceNo ?? '')),
        dnItemNo: isOrderMode
          ? (item?.dnItemNo ?? '')
          : (item?.referenceItemNo ?? item?.dnItemNo ?? ''),
        deliveryQty: Number(item?.dnQuantity ?? 0),
        submittableQty: Number(item?.availableQuantity ?? 0),
        claimQty: qty,
        claimAmount:
          item?.claimAmount != null && item?.claimAmount !== ''
            ? formatMoney(item.claimAmount, item?.currency)
            : formatMoney(qty * unitPrice, item?.currency),
        receiverNumber: item?.receiverNumber ?? '-',
        returnOrderNumber: item?.returnOrderNumber ?? '-',
        orderNo: isOrderMode ? (item?.orderNo ?? '') : undefined,
        orderItemNo: isOrderMode ? (item?.orderItemNo ?? '') : undefined,
        orderTotalAmount: isOrderMode
          ? formatMoney(Number(item?.dnTotalValueWithTax ?? 0), item?.currency)
          : undefined,
        orderQuantity: isOrderMode ? Number(item?.orderQuantity ?? item?.dnQuantity ?? 0) : undefined,
        invoiceNo: !isOrderMode ? (item?.invoiceNo ?? '') : undefined,
        invoiceItemNo: !isOrderMode ? (item?.invoiceItemNo ?? '') : undefined,
        dnTotalValueWithTax: !isOrderMode
          ? formatMoney(Number(item?.netValue ?? item?.dnTotalValueWithTax ?? 0), item?.currency)
          : undefined,
        dnQuantity: Number(item?.billedQty ?? item?.invoicedQuantity ?? item?.dnQuantity ?? 0),
        priceDifference: item?.priceDifference != null ? String(item.priceDifference) : undefined,
      });
    });
  });
  return rows;
}

function buildSelectedKey(item: any, refType: string, referenceNo?: string): string {
  const material = item?.materialCode ?? item?.materialNumber ?? '';
  if (refType === 'INVOICE') {
    const invoiceNo = item?.invoiceNo ?? referenceNo ?? '';
    const invoiceItemNo = item?.invoiceItemNo ?? '';
    return `${material}|${invoiceNo}|${invoiceItemNo}`;
  }
  const dnNo = item?.dnNo ?? referenceNo ?? '';
  const dnItemNo = item?.dnItemNo ?? '';
  return `${material}|${dnNo}|${dnItemNo}`;
}

function ensureOption(
  options: { label: string; value: string }[],
  value: string | null | undefined,
): void {
  if (!value) {
    return;
  }
  if (options.some(opt => opt.value === value)) {
    return;
  }
  options.push({ label: value, value });
}

function formatMoney(value: number, currency?: string): string {
  if (Number.isNaN(value)) {
    value = 0;
  }
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency ?? 'CNY'} ${formatted}`;
}

function pickFirstString(source: any, keys: string[]): string {
  for (const key of keys) {
    const v = source?.[key];
    if (v !== null && v !== undefined && `${v}`.trim() !== '') {
      return `${v}`;
    }
  }
  return '';
}
